import Screen from "@/components/layout/Screen";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import SignUpForm, { SignUpFormSkeleton } from "@/components/shared/SignUpComponents/SignUpForm";
import SignupVerification from "@/components/shared/SignUpComponents/SignUpVerification";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthNavigation } from "@/hooks/useAuthNavigation";
import { useToastNotify } from "@/stores/toastStore";
import { PHONE_FIELD_ERROR } from "@/utils/auth/cognitoErrors";
import { signUpSchema, verifyCodeSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react-native";
import { useT } from "@transifex/react";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { Platform } from "react-native";
import type { z } from "zod";

type SignUpSchemaType = z.infer<typeof signUpSchema>;

const SignUp = () => {
  const router = useRouter();
  const { verify, email } = useLocalSearchParams<{ verify?: string; email?: string }>();
  const { signUp, confirmSignUp, resendCode, signIn, isLoading, pendingEmail, setPendingEmail } = useAuth();
  const t = useT();
  const { notify } = useToastNotify();
  const [showVerification, setShowVerification] = useState(false);
  const [credentials, setCredentials] = useState<SignUpSchemaType | null>(null);
  const handleAuthNavigation = useAuthNavigation();
  const [verificationError, setVerificationError] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useFocusEffect(
    useCallback(() => {
      return () => {
        resetSignUpForm();
        resetField("code");
        setShowVerification(false);
        setVerificationError(undefined);
        setPhoneError(undefined);
        setCredentials(null);
        setPendingEmail(null);
        clearErrors();
        clearVerifyErrors();
      };
    }, [setPendingEmail]),
  );

  useFocusEffect(
    useCallback(() => {
      if (verify === "true") {
        if (email || pendingEmail?.email) {
          if (!showVerification) {
            setShowVerification(true);
          } else if (!credentials) {
            setCredentials({ email: email ?? pendingEmail?.email, password: pendingEmail?.password } as SignUpSchemaType);
            if (email) setPendingEmail({ email, type: "sign-up" });
          } else {
            resendCode();
          }
        } else {
          setShowVerification(false);
        }
      }
      return () => {
        resetField("code");
      };
    }, [pendingEmail, verify, email, showVerification, credentials, resendCode, setPendingEmail]),
  );

  const handleSuccessfulVerification = () => {
    setShowVerification(false);
  };

  const notifyError = (message: string) =>
    notify({
      severity: "error",
      message: t(message),
    });

  const {
    reset,
    clearErrors,
    reset: resetSignUpForm,
  } = useForm<SignUpSchemaType>({
    resolver: zodResolver(signUpSchema),
  });

  useFocusEffect(
    useCallback(() => {
      reset();
    }, [reset]),
  );

  type VerifyFormType = z.infer<typeof verifyCodeSchema>;

  const { clearErrors: clearVerifyErrors, resetField } = useForm<VerifyFormType>({
    resolver: zodResolver(verifyCodeSchema),
  });

  const onSubmit = async (data: SignUpSchemaType) => {
    setIsSubmitting(true);
    // Cleared up front so an identical repeat failure still registers as a change
    // and gets re-applied to the field.
    setPhoneError(undefined);
    try {
      const phoneWithUSCountryCode = data.phone ? `+${data.phone}` : null;
      await signUp(data.email, data.password, data.first, data.last, phoneWithUSCountryCode);
      setCredentials(data);
      setShowVerification(true);
      resetField("code");
      setVerificationError(undefined);
    } catch (err) {
      if (err instanceof Error && err.message.includes("already exists")) {
        notifyError("An account with this email already exists. Please sign in or reset your password.");
        router.replace("/auth/sign-in");
        return;
      }
      if (err instanceof Error && err.name === PHONE_FIELD_ERROR) {
        // Shown on the phone field instead of a toast, so it clears when the user
        // edits the number. Not reported: like the duplicate email above, this is
        // the user's to correct, not a failure.
        setPhoneError(err.message);
        return;
      }
      Sentry.captureException(err, {
        extra: {
          data,
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
      notifyError(err instanceof Error ? err.message : "An unknown error occurred during sign up.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onVerifySubmit = async (data: { code: string }) => {
    const emailToVerify = credentials ?? pendingEmail;
    if (!emailToVerify) {
      notifyError("No email available for verification.");
      return;
    }
    if (!credentials?.password) {
      notifyError("Missing password for automatic sign in.");
      router.replace("/auth/sign-in");
      return;
    }
    try {
      await confirmSignUp(emailToVerify.email, data.code);
      setPendingEmail(null);
      handleSuccessfulVerification();
      notify({
        severity: "info",
        message: t("Signing you in..."),
      });
      try {
        await signIn(emailToVerify.email, credentials.password);
        // Reset both forms
        resetSignUpForm();
        resetField("code");
        setVerificationError(undefined);
        handleAuthNavigation();
      } catch (signInError) {
        console.error("Wrong password after a sign-up verification, just redirecting to sign-in:", signInError);
        router.replace("/auth/sign-in");
      }
    } catch (err) {
      if (err instanceof Error && err.name) {
        switch (err.name) {
          case "CodeMismatchException":
            setVerificationError(t("The verification code you entered is incorrect. Please try again."));
            break;
          case "ExpiredCodeException":
            setVerificationError(t("The verification code has expired. Please request a new one."));
            break;
          case "LimitExceededException":
            setVerificationError(t("Too many attempts. Please wait and try again later."));
            break;
          default:
            setVerificationError(err.message ?? t("An unexpected error occurred."));
        }
      } else {
        setVerificationError(t("An unknown error occurred during verification."));
      }
      Sentry.captureException(err, {
        extra: {
          data,
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
    }
  };

  const handleResendCode = async () => {
    const emailToVerify = credentials?.email ?? pendingEmail?.email;
    if (!emailToVerify) {
      notifyError("No email available to resend the code.");
      return;
    }
    await resendCode();
  };

  // Not while submitting: swapping in the skeleton unmounts SignUpForm, discarding
  // everything the user typed along with any error set on its fields.
  if (isLoading && !showVerification && !isSubmitting) {
    return (
      <Screen>
        <SignUpFormSkeleton />
      </Screen>
    );
  }

  return (
    <Screen>
      <PageTitle title={showVerification ? t("Verify Your Account") : t("Sign Up")} />
      {showVerification || (pendingEmail?.email && pendingEmail.type === "sign-up") ? (
        <SignupVerification errorMessage={verificationError} onVerifySubmit={onVerifySubmit} onResendCode={handleResendCode} isLoading={isLoading} />
      ) : (
        <SignUpForm phoneErrorMessage={phoneError} onSubmit={onSubmit} isLoading={isLoading} />
      )}
    </Screen>
  );
};

export default SignUp;
